<script setup>

import {computed, onBeforeMount, onMounted, onUnmounted, ref, watch} from "vue";
import {useSessionStore} from "@/utils/useSessionStore.js";
import {useActiveCoupon} from "@/utils/useActiveCoupon.js";
import ActiveCouponLauncher from "@/components/ui/ActiveCouponLauncher.vue";
import CustomerChatWidget from "../../packages/CustomerChat/resources/js/CustomerChatWidget.vue";
import AppFooter from "@/AppFooter.vue";
import AppHeader from "@/AppHeader.vue";
import AppFooterDesktop from "@/AppFooterDesktop.vue";
import AppHeaderDesktop from "@/AppHeaderDesktop.vue";
import {useScreenHelper} from "@/plugins/ScreenHelper.js";
import {getLanguagePackage} from "@/i18n/useLanguage.js";
import {useRoute, useRouter} from "vue-router";
import {ArrowLeft, ArrowRight, MessageCircle, RefreshCw, Search, Truck} from "lucide-vue-next";
import CmsLoader from "@/components/cms/CmsLoader.vue";
import AppPopup from "@/AppPopup.vue";
import RoundButton from "@/components/form/RoundButton.vue";
import SwitchInput from "@/components/form/SwitchInput.vue";
import {locale} from "@/i18n/useLanguage.js";

const sessionStore = useSessionStore();
const activeCoupon = useActiveCoupon();
const openSideBar = ref(false);
const loading = ref(false);
const customerChatEnabled = ref(false);

function goToChromaworks() {
  window.location.href = 'https://chromaworks.co.kr'
}

function moveToTop() {
  if (!window) return;
  window.scrollTo({top: 0, behavior: 'smooth'})
}



onBeforeMount(async () => {
  await sessionStore.init();
  await refreshActiveCoupon();
});

const route = useRoute();

function refreshActiveCoupon() {
  return activeCoupon.refresh(sessionStore.authenticated);
}

function refreshWhenVisible() {
  if (document.visibilityState === 'visible') refreshActiveCoupon();
}

watch(() => route.fullPath, () => {
  if (!blankTemplate.value) refreshActiveCoupon();
});

const blankTemplate = computed(() => {
  // route.path 가 /admin 으로 시작
  return route.meta?.blank || route.path.startsWith('/admin');
})

const screenHelper = useScreenHelper();

onBeforeMount(async () => {
  await getLanguagePackage()
  loading.value = true;
});

onMounted(() => {
  customerChatEnabled.value = window.__CUSTOMER_CHAT_ENABLED__ === true;
  screenHelper.registerResizeEvent()
  registerScrollEvent();
  window.addEventListener('focus', refreshActiveCoupon);
  document.addEventListener('visibilitychange', refreshWhenVisible);
});

onUnmounted(() => {
  screenHelper.unregisterResizeEvent()
  unregisterScrollEvent();
  window.removeEventListener('focus', refreshActiveCoupon);
  document.removeEventListener('visibilitychange', refreshWhenVisible);
});


const customerChat = ref(null);
const router = useRouter();

const refreshPage = ()=>{
  window.location.reload();
}
const isShowFootBar = ref(true);
const showFootBar = ()=>{
  isShowFootBar.value = true;
}
const hideFootBar = ()=>{
  isShowFootBar.value = false;
}
const isScrolledBeyond40 = ref(false);

const handleScroll = () => {
  const scrollTop = window.scrollY;
  const winHeight = window.innerHeight;
  const scrollPercent = (scrollTop / winHeight) * 100;
  isScrolledBeyond40.value = scrollPercent > 40;
};

function registerScrollEvent() {
  if(!window) return;
  window.addEventListener('scroll', handleScroll);
}
function unregisterScrollEvent() {
  if(!window) return;
  window.removeEventListener('scroll', handleScroll);
}
const getScrollTopButtonStyle = computed(() => {
  return {
    opacity: isScrolledBeyond40.value ? 1 : 0,
    transform: isScrolledBeyond40.value ? 'scale(1)' : 'scale(0)',
  }
});

const isCandleworksHost = computed(() => {
  if (import.meta.env.SSR) {
    return false;
  }
  return window.location.host === 'candleworks.co.kr' || window.location.host === 'www.candleworks.co.kr';
});
</script>

<template>
  <AppPopup />
  <div v-if="blankTemplate">
    <router-view />
  </div>
  <div
    v-if="!blankTemplate"
    class="min-h-screen h-full mb-16 lg:mb-0"
  >
    <cms-loader
      name="TopBar"
      closable
    />
    <app-header-desktop v-if="screenHelper.isDesktop" />
    <app-header
      v-else
      v-model:side-bar="openSideBar"
    />
    <router-view
      v-slot="{Component}"
      class="w-full"
    >
      <keep-alive :max="3">
        <component :is="Component" />
      </keep-alive>
    </router-view>

    <app-footer-desktop v-if="screenHelper.isDesktop" />
    <app-footer v-else />
  </div>
  <footer
    :class="{ 'front-top' : isShowFootBar }"
    class="lg:hidden fixed h-16 bottom-0 left-0 p-1 w-full z-10 flex items-center justify-center border-t border-stone-300 bg-stone-100"
  >
    <ul class="flex justify-evenly w-full">
      <li><ArrowLeft @click="()=>{ router.back(); }" /></li>
      <li><ArrowRight @click="()=>{ router.go(1); }" /></li>
      <li><refresh-cw @click="()=>{ refreshPage() }" /></li>
      <li><truck @click="()=>{ router.push({ name: 'MyPageOrder' }) }" /></li>
      <li>
        <Search @click="()=>{ router.push({name:'ProductSearch'}) }" />
      </li>
      <li v-if="customerChatEnabled" class="relative">
        <active-coupon-launcher
          v-if="!blankTemplate"
          compact
          class="absolute bottom-full right-0 mb-4"
        />
        <button
          type="button"
          :aria-label="$t('채팅 상담')"
          @click.stop="()=>{customerChat.openTalk();}"
        >
          <MessageCircle aria-hidden="true" />
        </button>
      </li>
    </ul>
  </footer>
  <customer-chat-widget
    v-if="customerChatEnabled"
    ref="customerChat"
    @hide="showFootBar"
    @show="hideFootBar"
    :locale="locale"
  >
    <template #before-launcher>
      <active-coupon-launcher v-if="!blankTemplate" />
    </template>
    <template #browser-notification-switch="{enabled, disabled, label, toggle}">
      <switch-input
        :model-value="enabled"
        :disabled="disabled"
        :label="label"
        @update:model-value="toggle"
      />
    </template>
  </customer-chat-widget>
  <active-coupon-launcher
    v-if="!customerChatEnabled && !blankTemplate"
    class="hidden lg:flex fixed right-4 bottom-4 z-[200]"
  />
  <active-coupon-launcher
    v-if="!customerChatEnabled && !blankTemplate"
    compact
    class="lg:hidden fixed right-4 bottom-20 z-20"
  />
  <transition
    mode="in-out"
    name="fade"
    appear
  >
    <div
      class="arrow-up w-[3.5rem] h-[3.5rem] rounded flex items-center justify-center bg-opacity-60 bg-stone-600 shadow-sm transition-all duration-300 cursor-pointer"
      style="position: fixed;  z-index: 5"
      :style="getScrollTopButtonStyle"
      @click="()=>{ moveToTop() }"
    >
      <ArrowLeft class="text-white rotate-90" />
    </div>
  </transition>
  <div
    v-if="isCandleworksHost"
    class="fixed top-0 left-0 w-full h-full bg-black z-50 flex items-center justify-center bg-opacity-70"
  >
    <div class="flex flex-col items-center">
      <p class="lg:text-2xl text-lg p-4 text-white">
        캔들웍스를 사랑해 주시는 고객님,<br>
        방문해 주셔서 감사합니다!
        <br><br>
        고객님의 관심과 사랑으로 캔들웍스는 오래전 부터 캔들을 넘어선 홈프래그런스 브랜드였습니다.<br>
        그 사랑에 걸맞게 보다 깊이있는 스펙트럼과 제품을 선보이는 브랜드로 고객님과 함께 성장하고자<br>
        <br>
        <span class="text-gray-200 text-xl lg:text-2xl">candleworks.co.kr(캔들웍스)</span> 에서
        <br>
        <span class="text-stone-100 text-xl lg:text-2xl font-bold">chromaworks.co.kr(크로마웍스)</span> 로
        <br> 새롭게 출발합니다!
        <br><br>
        원활한 사이트 이용을 위해<br>
        chromaworks.co.kr(크로마웍스)로 접속해주세요.
        <br><br>
        앞으로도 고객님께 사랑받는 브랜드, CW 크로마웍스가 되겠습니다.<br>감사합니다.
        <br><br>
        <round-button
          class="bg-white p-4 self-center "
          @click="()=>{ goToChromaworks() }"
        >
          이동하기
        </round-button>
      </p>
    </div>
  </div>
</template>

<style>
.swiper-button-next, .swiper-button-prev {
  @apply bg-white rounded-full shadow-md w-8 h-8 flex items-center justify-center
}
.swiper-button-next:after, .swiper-button-prev:after{
  @apply text-stone-700 text-sm
}
.front-top{
  z-index: 1000;
}

.arrow-up{
  bottom : 1rem;
  right: 5rem;
}

@media screen and (max-width: 1000px) {
  .arrow-up{
    top : 4.5rem;
    right : 0.5rem;
  }
}
</style>
